import { ICellRendererParams } from '@ag-grid-community/core';
import { Body } from '@assembly-js/design-system';
import { IconButton, useMediaQuery, useTheme } from '@material-ui/core';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { ExternalLinkIcon } from 'src/components/icons';
import { COPYABLE_FIELDS_TYPES } from 'src/constants';
import AddressInput from 'src/features/crm/properties/AddressInput';
import {
  CustomFieldValue,
  CustomFieldValueData,
  isAddressCustomFieldValues,
} from 'src/legacy/components/ClientDetailsPage/clientDetailsTypes';
import { MapFieldTypeToDefaultProps } from 'src/legacy/components/CustomFieldsMenu';
import { MultiSelect } from 'src/legacy/components/MultiSelect';
import {
  QuickTextEditor,
  QuickTextEditorProps,
} from 'src/legacy/components/TextEditor/QuickTextEditor';
import { EditableCellValueWrapper } from 'src/legacy/components/UI/Tables/EditableCellWrapper';
import { CompanyWithClients } from 'src/routes/companies/table/utils';
import {
  AddressCustomField,
  ClientTableRowModel,
  CustomFieldOption,
  isAddressCustomField,
  isMultiSelectCustomField,
  MultiSelectCustomField,
} from 'src/store/clients/types';
import {
  BlackHeadings,
  HoverNonBorderTableEditableItemBackground,
} from 'src/theme/colors';
import { UrlUtils } from 'src/utils';
import {
  formatFieldValue,
  getFieldInputComponent,
  getFieldValidationByType,
} from 'src/utils/CustomFieldsUtils';

const useStyles = makeStyles()(() => ({
  fieldIconBtn: {
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: HoverNonBorderTableEditableItemBackground,
    },
    '& svg': {
      color: BlackHeadings,
      fontSize: '14px',
      fill: 'none',
    },
  },
}));

type CustomFieldsType = {
  [key: string]: CustomFieldValue | null | undefined;
};
export interface CustomFieldsRendererProps extends ICellRendererParams {
  customFieldData: MultiSelectCustomField | CustomFieldOption;
  data: (ClientTableRowModel | CompanyWithClients) & CustomFieldsType;
  setIsEdit?: (isEdit: boolean) => void;
  onCustomFieldValueUpdated: (customFieldData: CustomFieldValueData) => void;
  onCustomFieldPropertyUpdated: (
    customFieldProperty: CustomFieldOption,
    deletedOptionKey?: string,
  ) => Promise<void>;
}
export const CustomFieldsRenderer: React.FC<CustomFieldsRendererProps> =
  React.memo(
    ({
      customFieldData: field,
      onCustomFieldValueUpdated,
      onCustomFieldPropertyUpdated,
      data,
      setIsEdit = () => null,
    }) => {
      const theme = useTheme();
      const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
      // when multi select has empty tags, it notify the custom fields
      // renderer component in order to render field icon.
      const [forceShowFieldIcon, setForceShowFieldIcon] = React.useState(false);
      const { FieldIcon, inputComponent } = React.useMemo(
        () => ({
          FieldIcon: MapFieldTypeToDefaultProps[field.type].icon,
          inputComponent: getFieldInputComponent(field.type),
        }),
        [field.type],
      );
      const [editable, setEditable] = React.useState(false);
      const cellValue = React.useMemo(() => data[field.id], [data, field.id]);

      const { classes } = useStyles();
      /**
       * This function handle saving new custom field value
       * @param newValue custom field value to submit
       */
      // Type guards to check the structure of the data
      const isClientTableRowModel = (
        item: (ClientTableRowModel | CompanyWithClients) & CustomFieldsType,
      ): item is ClientTableRowModel & CustomFieldsType => {
        return item && typeof item === 'object' && 'clientFullData' in item;
      };

      const isCompanyWithClients = (
        item: (ClientTableRowModel | CompanyWithClients) & CustomFieldsType,
      ): item is CompanyWithClients & CustomFieldsType => {
        return (
          item &&
          typeof item === 'object' &&
          'clients' in item &&
          'fields' in item
        );
      };

      const getClientOrCompanyData = () => {
        if (isClientTableRowModel(data)) {
          return {
            updatingClient: data.clientFullData,
            updatingCompany: undefined,
          };
        } else if (isCompanyWithClients(data)) {
          return {
            updatingClient: undefined,
            updatingCompany: data,
          };
        }

        // Fallback - this should not happen in normal operation
        console.warn('Unknown data type in CustomFieldsRenderer:', data);
        return { updatingClient: undefined, updatingCompany: undefined };
      };

      const handleUpdateCustomFieldValue = (newValue: string) => {
        if (!field.id) {
          return;
        }
        const value = newValue?.trim();
        // if both values are falsey or
        // if value has not changed then nothing to update
        const skipUpdateCall = (!value && !cellValue) || value === cellValue;
        if (!skipUpdateCall) {
          const { updatingClient, updatingCompany } = getClientOrCompanyData();

          onCustomFieldValueUpdated({
            fieldId: field.id,
            value,
            updatingClient,
            updatingCompany,
          });
        }
        // hide quick text editor
        setEditable(false);
        setIsEdit(false);
      };

      const handleComplexCustomFieldValueChange = (
        customFieldValueData: CustomFieldValueData,
      ) => {
        const { fieldId, value } = customFieldValueData;
        const { updatingClient, updatingCompany } = getClientOrCompanyData();

        onCustomFieldValueUpdated({
          fieldId,
          value,
          updatingClient,
          updatingCompany,
        });
      };

      const inputActions = React.useMemo(() => {
        let actions: QuickTextEditorProps['inputActions'] = undefined;
        if (field.type === 'url') {
          actions = [
            {
              key: 'external-link-action',
              icon: ExternalLinkIcon,
              onClick: (
                _: React.MouseEvent<HTMLButtonElement>,
                linkUrl: string,
              ) => window.open(UrlUtils.GetFullUrl(linkUrl), '_blank'),
            },
          ];
        }

        return actions;
      }, [field.type]);

      /**
       * This function is used to switch custom field cell
       * to edit mode.
       */
      const setEditableTrue = () => {
        setEditable(true);
        setIsEdit(true);
      };

      /**
       * This renderer function is used to display
       * text field inputs.
       */
      const textCustomFieldRenderer = () => {
        if (editable) {
          return (
            <QuickTextEditor
              onUpdate={handleUpdateCustomFieldValue}
              canEdit={editable}
              onCancel={() => {
                setEditable(false);
                setIsEdit(false);
              }}
              onFocus={(ev) => {
                if (!ev) setEditable(false);
              }}
              // cellValue might not be string, if not we pass empty string value for processing
              value={typeof cellValue === 'string' ? cellValue : ''}
              showCopyIcon
              InputComponent={inputComponent}
              skipErrorOnUnfocused
              inputWrapperStyleProps={{
                width: 200,
                position: 'absolute',
                zIndex: 1,
              }}
              validator={(value: string) =>
                getFieldValidationByType(field.type, value)
              }
              inputActions={inputActions}
              canSubmitEmptyString
            />
          );
        }

        if (cellValue) {
          return (
            <EditableCellValueWrapper
              onClick={setEditableTrue}
              className="w-full"
            >
              <Body size="sm" className="overflow-hidden text-ellipsis ">
                {formatFieldValue(
                  field.type,
                  typeof cellValue === 'string' ? cellValue : '',
                )}
              </Body>
            </EditableCellValueWrapper>
          );
        }

        return null;
      };

      /**
       * This renderer is used to display multi select field
       * input.
       */
      const multiSelectCustomFieldRenderer = () => (
        <MultiSelect
          autoFocus={!isMobile}
          showPreviewPlaceholder={false}
          onTagsEmpty={(isEmpty) => setForceShowFieldIcon(isEmpty)}
          tagsLimit={1}
          // this prop is used to trigger open
          // tags menu from outside the multiselect
          // component itself. e.g. when user
          // clicks on field icon menu button
          // it should open the multi select menu.
          shouldOpenMenu={editable}
          isCellContext
          onClose={() => {
            setEditable(false);
            setIsEdit(false);
          }}
          placeholder="Add a tag"
          initialFieldValue={Array.isArray(cellValue) ? cellValue : []}
          field={
            isMultiSelectCustomField(field)
              ? field
              : (field as MultiSelectCustomField)
          }
          onSaveProperty={onCustomFieldPropertyUpdated}
          onSaveValue={handleComplexCustomFieldValueChange}
        />
      );

      const isCellValueEmpty =
        cellValue === null ||
        cellValue === undefined ||
        ((typeof cellValue === 'string' || Array.isArray(cellValue)) &&
          cellValue.length === 0) ||
        (typeof cellValue === 'object' &&
          Object.keys(cellValue).length === 0) ||
        (field.type === 'address' &&
          isAddressCustomFieldValues(cellValue) &&
          (cellValue.fullAddress === '' || cellValue.fullAddress === null));

      return (
        <div className="flex items-center">
          {(isCellValueEmpty || forceShowFieldIcon) && !editable && (
            <IconButton
              onClickCapture={setEditableTrue}
              className={classes.fieldIconBtn}
            >
              <FieldIcon />
            </IconButton>
          )}
          {COPYABLE_FIELDS_TYPES.includes(field.type) &&
            textCustomFieldRenderer()}
          {field.type === 'multiSelect' && multiSelectCustomFieldRenderer()}
          {field.type === 'address' && (
            <AddressInput
              field={
                isAddressCustomField(field)
                  ? field
                  : (field as AddressCustomField)
              }
              value={
                cellValue && isAddressCustomFieldValues(cellValue)
                  ? cellValue
                  : { fullAddress: '' }
              }
              placeholder={
                MapFieldTypeToDefaultProps[field.type]?.placeholder || ''
              }
              onSaveValue={handleComplexCustomFieldValueChange}
              onClose={() => {
                setEditable(false);
                setIsEdit(false);
              }}
              renderAsCell
              shouldOpenMenu={editable}
            />
          )}
        </div>
      );
    },
  );
